/* VENTARA visual primitives — Node, Path, Flow, Field, Spark.
   Pure SVG, no tech clichés, breathing motion.

   Each primitive supports `variant`:
     - "mono" (default): inherits `currentColor` — clean, editorial
     - "brand": paints with the Ventara brand gradient
       (Aurora Magenta → Solar Pink → Solar Orange, plus Quantum Violet).
*/
import { motion } from "framer-motion";
import { EASE } from "@/lib/motion";

/** Shared brand gradient <defs> — reused via url(#...) across the document.
 *  Mounting multiple times is a no-op; SVG resolves the last-parsed id. */
export const BrandDefs = () => (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden focusable="false">
        <defs>
            {/* Linear stroke gradient — used by Path, Flow, Node rings */}
            <linearGradient id="ventara-brand-stroke" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#8A2BE2" />
                <stop offset="50%" stopColor="#FF4DA6" />
                <stop offset="100%" stopColor="#FF8A00" />
            </linearGradient>
            {/* Radial fill gradient — used by Spark & Field */}
            <radialGradient id="ventara-brand-radial" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFB84D" stopOpacity="1" />
                <stop offset="35%" stopColor="#FF8A00" stopOpacity="0.75" />
                <stop offset="70%" stopColor="#FF4DA6" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#8A2BE2" stopOpacity="0" />
            </radialGradient>
            {/* Solid spark gradient */}
            <linearGradient id="ventara-brand-spark" x1="0.1" y1="0" x2="0.9" y2="1">
                <stop offset="0%" stopColor="#FF4DA6" />
                <stop offset="55%" stopColor="#FF8A00" />
                <stop offset="100%" stopColor="#FFB84D" />
            </linearGradient>
            {/* Dot palette references (hint only — colors picked per-callsite) */}
        </defs>
    </svg>
);

const brandStroke = (variant) => (variant === "brand" ? "url(#ventara-brand-stroke)" : "currentColor");
const brandRadial = (variant) => (variant === "brand" ? "url(#ventara-brand-radial)" : "currentColor");
const brandSpark  = (variant) => (variant === "brand" ? "url(#ventara-brand-spark)"  : "currentColor");

/** Node — a single point of potential. Concentric quiet circles. */
export const Node = ({ className = "", size = 140, variant = "mono" }) => (
    <svg viewBox="0 0 140 140" width={size} height={size} className={className} aria-hidden>
        {variant === "brand" && <BrandDefs />}
        <circle cx="70" cy="70" r="4" fill={brandSpark(variant)} />
        <motion.circle
            cx="70" cy="70" r="18"
            fill="none" stroke={brandStroke(variant)} strokeOpacity={variant === "brand" ? 0.8 : 0.35} strokeWidth="1"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: EASE }}
        />
        <motion.circle
            cx="70" cy="70" r="42"
            fill="none" stroke={brandStroke(variant)} strokeOpacity={variant === "brand" ? 0.45 : 0.15} strokeWidth="1"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.15 }}
        />
        <motion.circle
            cx="70" cy="70" r="66"
            fill="none" stroke={brandStroke(variant)} strokeOpacity={variant === "brand" ? 0.2 : 0.07} strokeWidth="1"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.6, ease: EASE, delay: 0.3 }}
        />
    </svg>
);

/** Path — a bezier that draws itself. Connection. */
export const Path = ({ className = "", variant = "mono" }) => (
    <svg viewBox="0 0 400 140" className={className} aria-hidden preserveAspectRatio="none">
        {variant === "brand" && <BrandDefs />}
        <motion.path
            d="M0,90 C 90,20 180,140 260,60 S 380,80 400,50"
            fill="none" stroke={brandStroke(variant)} strokeWidth={variant === "brand" ? 1.75 : 1.25} strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2.4, ease: EASE }}
        />
        <circle cx="0" cy="90" r="3" fill={variant === "brand" ? "#8A2BE2" : "currentColor"} />
        <circle cx="400" cy="50" r="3" fill={variant === "brand" ? "#FF8A00" : "currentColor"} />
    </svg>
);

/** Flow — several calm parallel curves drifting. Transformation. */
export const Flow = ({ className = "", variant = "mono" }) => (
    <svg viewBox="0 0 600 220" className={className} aria-hidden preserveAspectRatio="none">
        {variant === "brand" && <BrandDefs />}
        {[0, 1, 2, 3, 4].map((i) => (
            <motion.path
                key={i}
                d={`M0,${40 + i * 28} C 150,${20 + i * 28} 300,${80 + i * 28} 450,${40 + i * 28} S 600,${60 + i * 28} 600,${50 + i * 28}`}
                fill="none"
                stroke={brandStroke(variant)}
                strokeOpacity={variant === "brand" ? 0.7 - i * 0.11 : 0.35 - i * 0.05}
                strokeWidth={variant === "brand" ? 1.4 : 1}
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2.6, ease: EASE, delay: i * 0.12 }}
            />
        ))}
    </svg>
);

/** Field — soft radial gradient over a subtle grid. Clarity. */
export const Field = ({ className = "", variant = "mono" }) => (
    <svg viewBox="0 0 400 400" className={className} aria-hidden>
        <defs>
            {variant === "brand" ? (
                <>
                    <radialGradient id="ventara-field" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#FF8A00" stopOpacity="0.55" />
                        <stop offset="35%" stopColor="#FF4DA6" stopOpacity="0.28" />
                        <stop offset="70%" stopColor="#8A2BE2" stopOpacity="0.08" />
                        <stop offset="100%" stopColor="#6A5CFF" stopOpacity="0" />
                    </radialGradient>
                </>
            ) : (
                <radialGradient id="ventara-field" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
                    <stop offset="55%" stopColor="currentColor" stopOpacity="0.05" />
                    <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                </radialGradient>
            )}
            <pattern id="ventara-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeOpacity="0.08" strokeWidth="0.5" />
            </pattern>
            <mask id="ventara-mask">
                <rect width="400" height="400" fill="url(#ventara-field)" />
            </mask>
        </defs>
        <rect width="400" height="400" fill="url(#ventara-grid)" mask="url(#ventara-mask)" />
        {variant === "brand" && (
            <rect width="400" height="400" fill="url(#ventara-field)" opacity="0.55" />
        )}
    </svg>
);

/** Spark — a 4-point brand star (brand mode) or radiant halo (mono). */
export const Spark = ({ className = "", variant = "mono" }) => (
    <svg viewBox="0 0 200 200" className={className} aria-hidden>
        {variant === "brand" && <BrandDefs />}
        {variant === "brand" ? (
            <motion.path
                d="M 100 20 Q 108 92 180 100 Q 108 108 100 180 Q 92 108 20 100 Q 92 92 100 20 Z"
                fill={brandSpark(variant)}
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: [0.4, 1.1, 1], opacity: [0, 1, 1] }}
                transition={{ duration: 2.4, ease: EASE }}
                style={{ transformOrigin: "50% 50%" }}
            />
        ) : (
            <>
                <defs>
                    <radialGradient id="ventara-spark-mono" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
                        <stop offset="40%" stopColor="currentColor" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                    </radialGradient>
                </defs>
                <motion.circle
                    cx="100" cy="100" r="60" fill="url(#ventara-spark-mono)"
                    initial={{ scale: 0.4, opacity: 0 }}
                    animate={{ scale: [0.4, 1.05, 1], opacity: [0, 1, 0.85] }}
                    transition={{ duration: 2.4, ease: EASE }}
                />
                <circle cx="100" cy="100" r="3" fill="currentColor" />
            </>
        )}
    </svg>
);

/** HeroConstellation — the calm opening animation for Home.
 *  Points connect into a flowing abstract form. No cliché. */
export const HeroConstellation = ({ className = "", variant = "mono" }) => {
    const points = [
        [12, 68], [22, 42], [34, 58], [46, 30], [58, 54],
        [70, 38], [82, 62], [90, 44],
    ];
    return (
        <svg viewBox="0 0 100 90" className={className} aria-hidden preserveAspectRatio="none">
            <defs>
                {variant === "brand" ? (
                    <linearGradient id="hc-stroke" x1="0" x2="1">
                        <stop offset="0%" stopColor="#8A2BE2" stopOpacity="0.3" />
                        <stop offset="50%" stopColor="#FF4DA6" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#FF8A00" stopOpacity="0.3" />
                    </linearGradient>
                ) : (
                    <linearGradient id="hc-stroke" x1="0" x2="1">
                        <stop offset="0%" stopColor="currentColor" stopOpacity="0.05" />
                        <stop offset="50%" stopColor="currentColor" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0.05" />
                    </linearGradient>
                )}
            </defs>
            <motion.path
                d={`M${points.map(p => p.join(",")).join(" L ")}`}
                fill="none"
                stroke="url(#hc-stroke)"
                strokeWidth="0.2"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 3.2, ease: EASE, delay: 0.2 }}
            />
            {points.map(([x, y], i) => (
                <motion.circle
                    key={i} cx={x} cy={y} r="0.55"
                    fill={variant === "brand" ? "#FF4DA6" : "currentColor"}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 0.85, scale: 1 }}
                    transition={{ duration: 1.2, ease: EASE, delay: 0.35 + i * 0.12 }}
                />
            ))}
        </svg>
    );
};
